import {
  Input,
  OnInit,
  inject,
  DestroyRef,
  Component,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { SuiPopoverTriggerDirective } from '@sui/popover';

import { selectPreviousPeriod } from '../../../core/actions/core.actions';
import { DashboardPagePersistenceService } from '../../../core/services/dashboard-page-persistence.service';

import {
  selectCoreLanguage,
  selectCurrentObject,
  selectEffectivePeriodForPage,
  selectCompanyDateBounds,
} from '../../../core/reducers';
import { AppState } from 'src/app/reducers';

import { toUnix } from '../../utils/date-utils';
import { DataObject, Period } from '../../../core/models/core.models';

import { addDays } from 'date-fns';

import { Observable, tap } from 'rxjs';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-date-range-picker-dashboard-page',
  templateUrl: './date-range-picker-dashboard-page.component.html',
  styleUrl: './date-range-picker-dashboard-page.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DateRangePickerDashboardPageComponent implements OnInit {
  @Input() pageId: number;

  @ViewChild(SuiPopoverTriggerDirective)
  private dropdown: SuiPopoverTriggerDirective;

  period$: Observable<Period>;
  periodHistory$: Observable<Period[]>;

  // ! we do not have current Object now
  currentObject$: Observable<DataObject>;

  fromDate = new Date();
  toDate = addDays(this.fromDate, 2);

  companyBounds$: Observable<{ min: Date | null; max: Date }>;
  isRtl: boolean;

  faCalendar = faCalendar;

  private destroyRef = inject(DestroyRef);
  private pagePersistence = inject(DashboardPagePersistenceService);

  constructor(
    private store: Store<AppState>,
    private cdf: ChangeDetectorRef,
  ) {
    this.currentObject$ = this.store.select(selectCurrentObject);
    this.companyBounds$ = this.store.select(selectCompanyDateBounds);

    this.store
      .select(selectCoreLanguage)
      .pipe(
        tap((language) => {
          this.isRtl = language.rtl;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit(): void {
    if (!this.pageId) {
      return;
    }

    // * use Page-specific selector with inheritance logic
    this.period$ = this.store.select(selectEffectivePeriodForPage(this.pageId));

    this.period$
      .pipe(
        tap((period) => {
          if (period) {
            this.fromDate = new Date(period.startTimestamp * 1000);
            this.toDate = new Date(period.endTimestamp * 1000);

            this.cdf.detectChanges();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  applyDateRange() {
    const period = {
      startTimestamp: toUnix(this.fromDate),
      endTimestamp: toUnix(this.toDate),
    };

    // Page-specific, not the global periodChanged. The service dispatches the
    // optimistic `updateDashboardPagePeriod` and then persists.
    this.pagePersistence.updatePagePeriod(this.pageId, period);

    this.cancel();
  }

  onDateRangeSelection(range: { from: Date; to: Date }) {
    this.fromDate = range.from;
    this.toDate = range.to;
  }

  cancel() {
    if (this.dropdown) {
      this.dropdown.close();
    }
  }

  back() {
    this.store.dispatch(selectPreviousPeriod());
  }
}
